import React, { useCallback, useMemo, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import { useMsal } from "@azure/msal-react";
import SharePointService from "./services/sharepointService";
import { fetchHighestDocId } from "./services/docIdService";
import { submitFormData } from "./services/formSubmissionService";
import { Box, Paper, Typography, Snackbar, Grid } from "@mui/material";
import FormContent from "../components/data-entry/FormContent";
// import CustomDatePicker from "../components/data-entry/CustomDatePicker";
import SubmittedDocuments from "../components/data-entry/SubmittedDocuments";
import PdfDisplay from "../components/data-entry/PdfDisplay";
import { getAccountByTenantId, getAuthenticatedAccount } from "../utils/accountUtils";
import { fetchDropdownOptions } from "./services/dropdownService";
// import PdfFileNameGenerator from "../components/data-entry/PdfFileNameGenerator";

const schema = yup.object().shape({
  pdfFile: yup.mixed().required("PDF file is required"),
  docId: yup.string().required("Document ID is required"),
  title1: yup.string().required("Title is required"),
  title: yup.string().nullable(),
  reportDate: yup.date().nullable(),
  sitename1: yup.string().nullable(),
  sitename2: yup.string().nullable(),
  sitename3: yup.string().nullable(),
  streetNumber: yup.string().nullable(),
  addressname: yup.string().nullable(),
  berth: yup.string().nullable(),
  building: yup.string().nullable(),
  portarea: yup.array().of(yup.string()),
  division: yup.string().nullable(),
  category: yup.string().nullable(),
  pdfName: yup.string().nullable(),
  generatedFileName: yup.string().nullable(),
  Report_x0020_Submittee: yup.string().nullable(),
});

const DataEntryForm = () => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      pdfFile: null,
      docId: "",
      title1: "",
      title: "",
      reportDate: null,
      sitename1: "",
      sitename2: "",
      sitename3: "",
      streetNumber: "",
      addressname: "",
      berth: "",
      building: "",
      portarea: [],
      division: "",
      category: "",
      pdfName: "",
      generatedFileName: "",
      Report_x0020_Submittee: "",
    },
    mode: "onChange",
  });

  const { instance, accounts } = useMsal();
  const tenantId = process.env.REACT_APP_AUTH_TENENT_ID;
  const account = useMemo(() => getAccountByTenantId(accounts, tenantId), [accounts, tenantId]);
  
  const sharePointService = useMemo(
    () => new SharePointService(instance, account),
    [instance, account]
  );

  const [showToast, setShowToast] = React.useState(false);
  const [showErrorToast, setShowErrorToast] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [submittedDocuments, setSubmittedDocuments] = React.useState([]);
  const [isLoadingDocId, setIsLoadingDocId] = React.useState(false);
  const [pdfName, setPdfName] = React.useState("");
  const pdfFile = watch("pdfFile");
  const [formOptions, setFormOptions] = useState({
    title: [],
    sitename1: [],
    sitename2: [],
    sitename3: [],
    addressname: [],
    division: [],
    category: [],
    portarea: [],
  });
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [unenteredDocumentId, setUnenteredDocumentId] = useState(null);

  const handleUnenteredDocumentSelect = useCallback(async (document) => {
    try {
      if (!document || !document.id) {
        throw new Error('Invalid document selected');
      }

      // Store the document ID to track that this file came from Unentered Documents
      setUnenteredDocumentId(document.id);

      let downloadUrl = document['@microsoft.graph.downloadUrl'];
      
      // If downloadUrl is not available, we need to get it
      if (!downloadUrl) {
        const account = await getAuthenticatedAccount(instance, accounts, tenantId);
        const response = await instance.acquireTokenSilent({
          scopes: [process.env.REACT_APP_AUTH_SP_SCOPE],
          account: account,
        });

        const graphApiUrl = process.env.REACT_APP_GRAPH_API_URL;
        const siteId = process.env.REACT_APP_SP_SITE_ID;

        // First get the download URL
        const fileResponse = await fetch(`${graphApiUrl}/sites/${siteId}/drive/items/${document.id}?$select=@microsoft.graph.downloadUrl`, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!fileResponse.ok) {
          throw new Error('Failed to get download URL');
        }

        const fileData = await fileResponse.json();
        downloadUrl = fileData['@microsoft.graph.downloadUrl'];

        // Then get the creator information
        const creatorResponse = await fetch(`${graphApiUrl}/sites/${siteId}/drive/items/${document.id}?$select=createdBy`, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (creatorResponse.ok) {
          const creatorData = await creatorResponse.json();
          if (creatorData.createdBy?.user?.displayName) {
            setValue("Report_x0020_Submittee", creatorData.createdBy.user.displayName);
          }
        }
      }

      if (!downloadUrl) {
        throw new Error('Could not get download URL for the file');
      }

      // Fetch the PDF file from the downloadUrl
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch PDF file');
      }
      const blob = await response.blob();
      const file = new File([blob], document.name, { type: 'application/pdf' });

      // Update form values
      setValue("pdfFile", file);
      setValue("pdfName", document.name);
      setPdfName(document.name);
    } catch (error) {
      console.error("Error handling unentered document selection:", error);
      setErrorMessage(error.message || "Failed to load selected document");
      setShowErrorToast(true);
    }
  }, [setValue, instance, accounts, tenantId]);

  const handleAddOption = useCallback((newOption, fieldName) => {
    setFormOptions(prevOptions => ({
      ...prevOptions,
      [fieldName]: [newOption.name, ...prevOptions[fieldName]]
    }));
    setTimeout(() => {
      setValue(fieldName, newOption.name, { shouldValidate: true });
    }, 0);
  }, [setValue]);


  const sanitizeFileName = (fileName) => {
    return fileName.replace(/[#&<>|?:*]/g, '_');
  };

  useEffect(() => {
    if (!isSubmitting) {
      // Reset form state
      setUnenteredDocumentId(null);
    }
  }, [isSubmitting]);

  useEffect(() => {
    const loadDropdownOptions = async () => {
      try {
        setIsLoadingOptions(true);
        const options = await fetchDropdownOptions(instance, accounts, tenantId);
        setFormOptions(options);
      } catch (error) {
        console.error("Error loading form data:", error);
        setErrorMessage("Failed to load form data. Please try refreshing the page.");
        setShowErrorToast(true);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    if (instance && accounts && tenantId) {
      loadDropdownOptions();
    }
  }, [instance, accounts, tenantId]);

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files[0];
      if (file && file.type === "application/pdf") {
        const sanitizedFileName = sanitizeFileName(file.name);
        const sanitizedFile = new File([file], sanitizedFileName, {
          type: file.type
        });
        
        // Clear unentered document ID since this is a manual upload
        setUnenteredDocumentId(null);
        
        setValue("pdfFile", sanitizedFile);
        const originalName = sanitizedFileName;
        // Keep original filename in pdfName
        setValue("pdfName", originalName);
        setPdfName(originalName);
      }
    },
    [setValue]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const file = e.dataTransfer.files[0];
      if (file && file.type === "application/pdf") {
        const sanitizedFileName = sanitizeFileName(file.name);
        const sanitizedFile = new File([file], sanitizedFileName, {
          type: file.type
        });
        
        // Clear unentered document ID since this is a manual upload
        setUnenteredDocumentId(null);
        
        setValue("pdfFile", sanitizedFile);
        const originalName = sanitizedFileName;
        // Keep original filename in pdfName
        setValue("pdfName", originalName);
        setPdfName(originalName);
      }
    },
    [setValue]
  );

  const onSubmit = useCallback(
    async (data) => {
      try {
        if (!data.generatedFileName) {
          setErrorMessage("Please generate a filename before submitting the document");
          setShowErrorToast(true);
          return;
        }

        const result = await submitFormData({
          data: {
            ...data,
            unenteredDocumentId: unenteredDocumentId
          },
          sharePointService,
          instance,
          accounts,
          reset,
        });

        if (result && result.success) {
          setPdfName("");
          setSubmittedDocuments(prevDocs => [
            {
              docId: result.docId,
              Title1: result.Title1,
              downloadLink: result.downloadLink
            },
            ...prevDocs
          ]);
          
          setShowToast(true);
          reset({
            ...data,
            pdfFile: null,
            pdfName: "",
            generatedFileName: ""
          });
          setUnenteredDocumentId(null);
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        setErrorMessage(error.message || "Failed to submit document. Please try again.");
        setShowErrorToast(true);
      }
    },
    [reset, sharePointService, instance, accounts, unenteredDocumentId]
  );

  const handleCloseToast = () => {
    setShowToast(false);
  };

  const handleCloseErrorToast = () => {
    setShowErrorToast(false);
  };

  const handleFetchHighestDocId = useCallback(async () => {
    try {
      setIsLoadingDocId(true);
      await fetchHighestDocId(instance, accounts, setValue, tenantId);
    } catch (error) {
      console.error("Error in handleFetchHighestDocId:", error);
    } finally {
      setIsLoadingDocId(false);
    }
  }, [instance, accounts, setValue, tenantId]);

  const handleKeyDown = useCallback((e) => {
    // Only prevent Enter key behavior for dropdown/select elements
    if (e.key === 'Enter' && (e.target.tagName === 'SELECT' || e.target.getAttribute('role') === 'combobox')) {
      e.preventDefault();
    }
  }, []);

  return (
    <Box sx={{margin: "0 auto", p: 2 }}>
      <Grid container spacing={2}>
        <Grid size={6.5}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <FormContent
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              handleDragOver={handleDragOver}
              handleDrop={handleDrop}
              handleFileChange={handleFileChange}
              handleKeyDown={handleKeyDown}
              pdfName={pdfName}
              errors={errors}
              control={control}
              setValue={setValue}
              formOptions={formOptions}
              isSubmitting={isSubmitting}
              fetchHighestDocId={handleFetchHighestDocId}
              isLoadingDocId={isLoadingDocId}
              isLoadingOptions={isLoadingOptions}
              instance={instance}
              accounts={accounts}
              tenantId={tenantId}
              onAddAuthor={(newAuthor) => handleAddOption(newAuthor, 'title')}
              onAddSitename1={(newOption) => handleAddOption(newOption, 'sitename1')}
              onAddSitename2={(newOption) => handleAddOption(newOption, 'sitename2')}
              onAddSitename3={(newOption) => handleAddOption(newOption, 'sitename3')}
              onAddAddressname={(newOption) => handleAddOption(newOption, 'addressname')}
              onUnenteredDocumentSelect={handleUnenteredDocumentSelect}
            />
          </Paper>
        </Grid>
        <Grid size={5.5}>
          <Paper variant="outlined" sx={{ p: 1, height: "100%", bgcolor: "grey.50" }}>
            <PdfDisplay pdfFile={pdfFile} />
          </Paper>
        </Grid>
      </Grid>
      <Box sx={{ mt: 2 }}>
        <SubmittedDocuments documents={submittedDocuments} />
      </Box>
      <Snackbar
        open={showToast}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        message={
          <Box>
            <Typography>Document successfully uploaded!</Typography>
          </Box>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <Snackbar
        open={showErrorToast}
        autoHideDuration={6000}
        onClose={handleCloseErrorToast}
        message={
          <Box>
            <Typography color="error">{errorMessage}</Typography>
          </Box>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default DataEntryForm;
